import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {

  constructor() { }

  exportToExcel(data: any[], fileName: string, sheetName: string = 'Sheet1'): void {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);

    // Auto-adjust column widths
    const columnWidths = this.getColumnWidths(data);
    worksheet['!cols'] = columnWidths;

    const workbook: XLSX.WorkBook = {
      Sheets: { [sheetName]: worksheet },
      SheetNames: [sheetName]
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    this.saveAsExcelFile(excelBuffer, fileName);
  }

  private getColumnWidths(data: any[]): { wch: number }[] {
    const headers = Object.keys(data[0]);
    return headers.map(header => {
      const maxLength = Math.max(
        header.length,
        ...data.map(row => {
          const value = row[header];
          return value ? String(value).length : 0;
        })
      );
      return { wch: Math.min(maxLength + 2, 50) };
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.xlsx`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  exportToCsv(data: any[], fileName: string): void {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvRows: string[] = [];

    // Add header row
    csvRows.push(headers.join(','));

    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header];
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        const escaped = String(value ?? '').replace(/"/g, '""');
        return /[,"\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
      });
      csvRows.push(values.join(','));
    }

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  formatDataForExport(data: any[], reportType: string): any[] {
    switch (reportType) {
      case 'driver':
        return data.map(item => ({
          'Driver ID': item.driverId,
          'Driver Name': item.driverName,
          'License Number': item.licenseNumber,
          'Phone Number': item.phoneNumber,
          'Total Trips': item.totalTrips,
          'Completed Trips': item.completedTrips,
          'Status': item.status,
          'Join Date': item.joinDate
        }));
      case 'truck':
        return data.map(item => ({
          'Truck ID': item.truckId,
          'Plate Number': item.plateNumber,
          'Model': item.model,
          'Capacity': item.capacity,
          'Total Trips': item.totalTrips,
          'Mileage (km)': item.mileage,
          'Status': item.status,
          'Last Maintenance Date': item.lastMaintenanceDate
        }));
      case 'customer':
        return data.map(item => ({
          'Customer ID': item.customerId,
          'Customer Name': item.customerName,
          'Company Name': item.companyName,
          'Email': item.email,
          'Phone Number': item.phoneNumber,
          'Total Orders': item.totalOrders,
          'Total Revenue ($)': item.totalRevenue,
          'Status': item.status,
          'Registration Date': item.registrationDate
        }));
      case 'consignee':
        return data.map(item => ({
          'Consignee ID': item.consigneeId,
          'Consignee Name': item.consigneeName,
          'Company Name': item.companyName,
          'Address': item.address,
          'Phone Number': item.phoneNumber,
          'Total Deliveries': item.totalDeliveries,
          'Status': item.status,
          'Created Date': item.createdDate
        }));
      default:
        return data;
    }
  }
}
